import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReconciliationPanel = ({ homeMedications, hospitalOrders, onReconcile }) => {
  const [selectedHome, setSelectedHome] = useState([]);
  const [selectedHospital, setSelectedHospital] = useState([]);
  const [discrepancies, setDiscrepancies] = useState([]);

  const handleCompare = () => {
    const foundDiscrepancies = [];
    
    homeMedications?.forEach(homeMed => {
      const matchingHospital = hospitalOrders?.find(
        hospMed => hospMed?.name?.toLowerCase() === homeMed?.name?.toLowerCase()
      );
      
      if (matchingHospital) {
        if (matchingHospital?.dosage !== homeMed?.dosage || 
            matchingHospital?.frequency !== homeMed?.frequency) {
          foundDiscrepancies?.push({
            id: `disc-${homeMed?.id}`,
            type: 'dosage-mismatch',
            medication: homeMed?.name,
            home: `${homeMed?.dosage} ${homeMed?.frequency}`,
            hospital: `${matchingHospital?.dosage} ${matchingHospital?.frequency}`,
            severity: 'warning'
          });
        }
      } else {
        foundDiscrepancies?.push({
          id: `disc-${homeMed?.id}`,
          type: 'missing-hospital',
          medication: homeMed?.name,
          home: `${homeMed?.dosage} ${homeMed?.frequency}`,
          hospital: 'Not ordered',
          severity: 'critical'
        });
      }
    });

    hospitalOrders?.forEach(hospMed => {
      const matchingHome = homeMedications?.find(
        homeMed => homeMed?.name?.toLowerCase() === hospMed?.name?.toLowerCase()
      );
      
      if (!matchingHome) {
        foundDiscrepancies?.push({
          id: `disc-${hospMed?.id}`,
          type: 'new-hospital',
          medication: hospMed?.name,
          home: 'Not on home list',
          hospital: `${hospMed?.dosage} ${hospMed?.frequency}`,
          severity: 'info'
        });
      }
    });

    setDiscrepancies(foundDiscrepancies);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-destructive/10 border-destructive/20 text-destructive';
      case 'warning':
        return 'bg-warning/10 border-warning/20 text-warning';
      case 'info':
        return 'bg-primary/10 border-primary/20 text-primary';
      default:
        return 'bg-muted border-border text-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 md:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
          <Icon name="GitCompare" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground">Medication Reconciliation</h2>
          <p className="text-sm text-muted-foreground">Compare home medications with hospital orders</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Home" size={16} />
            Home Medications ({homeMedications?.length})
          </h3>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {homeMedications?.map((med) => (
              <div key={med?.id} className="bg-card rounded-lg p-3 border border-border">
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={selectedHome?.includes(med?.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setSelectedHome([...selectedHome, med?.id]);
                      } else {
                        setSelectedHome(selectedHome?.filter(id => id !== med?.id));
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{med?.name}</p>
                    <p className="text-xs text-muted-foreground">{med?.dosage} - {med?.frequency}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Building2" size={16} />
            Hospital Orders ({hospitalOrders?.length})
          </h3>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {hospitalOrders?.map((med) => (
              <div key={med?.id} className="bg-card rounded-lg p-3 border border-border">
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={selectedHospital?.includes(med?.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        setSelectedHospital([...selectedHospital, med?.id]);
                      } else {
                        setSelectedHospital(selectedHospital?.filter(id => id !== med?.id));
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{med?.name}</p>
                    <p className="text-xs text-muted-foreground">{med?.dosage} - {med?.frequency}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Button
          variant="default"
          iconName="GitCompare"
          iconPosition="left"
          onClick={handleCompare}
        >
          Compare Lists
        </Button>
        <Button
          variant="outline"
          iconName="CheckCircle"
          iconPosition="left"
          onClick={() => onReconcile({ selectedHome, selectedHospital, discrepancies })}
        >
          Complete Reconciliation
        </Button>
      </div>
      {discrepancies?.length > 0 && (
        <div className="border-t border-border pt-6">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
            Identified Discrepancies ({discrepancies?.length})
          </h3>
          <div className="space-y-3">
            {discrepancies?.map((disc) => (
              <div
                key={disc?.id}
                className={`border rounded-lg p-4 ${getSeverityColor(disc?.severity)}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-sm font-semibold">{disc?.medication}</p>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-card/50">
                    {disc?.type?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ')}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="font-medium mb-1">Home:</p>
                    <p className="opacity-90">{disc?.home}</p>
                  </div>
                  <div>
                    <p className="font-medium mb-1">Hospital:</p>
                    <p className="opacity-90">{disc?.hospital}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReconciliationPanel;